import React from 'react';
import { isDarkBackground } from '../../utils/contrast';

export interface PlatformAttributionProps {
  /**
   * Background color of the preceding section, footer, or site canvas.
   * If provided, the attribution bar will harmonize with this background
   * while ensuring strict WCAG-compliant contrast.
   */
  bgColor?: string;
  /** Explicit override to force dark or light theme */
  isDark?: boolean;
  /** Optional custom class names */
  className?: string;
}

export const PLATFORM_ATTRIBUTION_URL = 'https://www.adiosoft.com/';
export const PLATFORM_ATTRIBUTION_TEXT = 'Adiosoft Website Builder ile yapılmıştır';

/**
 * PlatformAttribution - Zorunlu Platform İmzası
 * 
 * Adiosoft Website Builder ile oluşturulan tüm web sitelerinin en altında
 * standart ve silinemez bir platform özelliği olarak render edilir.
 * Müşteri footer'ından bağımsızdır ve her sektör/şablonda aktiftir.
 */
export const PlatformAttribution: React.FC<PlatformAttributionProps> = ({
  bgColor,
  isDark: explicitIsDark,
  className = '',
}) => {
  // Determine if background should be dark
  const isDark =
    typeof explicitIsDark === 'boolean'
      ? explicitIsDark
      : bgColor
      ? isDarkBackground(bgColor)
      : true;

  // Harmonize background with footer/page or default to sophisticated tone
  const resolvedBg = bgColor || (isDark ? '#020617' : '#F8FAFC');
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)';

  return (
    <div
      id="platform-attribution"
      data-platform="adiosoft-builder"
      aria-label="Platform Bilgisi"
      className={`w-full py-4 px-4 text-center border-t select-none transition-colors ${className}`}
      style={{
        backgroundColor: resolvedBg,
        borderColor: borderColor,
      }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-center">
        <a
          id="adiosoft-attribution-link"
          href={PLATFORM_ATTRIBUTION_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-block text-xs font-normal tracking-normal transition-all duration-200 hover:underline underline-offset-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-xs ${
            isDark
              ? 'text-slate-400 hover:text-slate-100 focus:ring-offset-slate-900'
              : 'text-slate-500 hover:text-slate-900 focus:ring-offset-white'
          }`}
          title="Adiosoft Website Builder"
        >
          {PLATFORM_ATTRIBUTION_TEXT}
        </a>
      </div>
    </div>
  );
};

export default PlatformAttribution;
